import { Minus, Plus, Users, Share2, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { toast } from "sonner";
import { useServings } from "@/context/ServingsContext";

export default function ServingsAdjuster() {
  const { servings, setServings, baseServings } = useServings();
  const [copied, setCopied] = useState(false);
  const dec = () => setServings(Math.max(1, servings - 1));
  const inc = () => setServings(Math.min(24, servings + 1));

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: document.title, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setCopied(true);
      toast.success(`Link copied — serves ${servings}`, {
        description: "Friends will see quantities scaled to this size.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        data-testid="servings-adjuster"
        className="fixed bottom-6 right-6 md:bottom-10 md:right-10 z-50"
      >
        <div className="backdrop-blur-xl bg-background/90 border border-border shadow-2xl rounded-full pl-5 pr-2 py-2 flex items-center gap-4">
          <div className="flex items-center gap-2 text-foreground/70">
            <Users className="w-4 h-4" strokeWidth={1.5} />
            <span className="font-serif-alt text-sm hidden sm:inline">Serves</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={dec}
              data-testid="decrease-servings-btn"
              disabled={servings <= 1}
              aria-label="Decrease servings"
              className="w-9 h-9 rounded-full bg-secondary hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors duration-300"
            >
              <Minus className="w-4 h-4" strokeWidth={2} />
            </button>
            <motion.span
              key={servings}
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 25 }}
              data-testid="servings-count"
              className="font-display text-3xl md:text-4xl w-10 text-center tabular-nums text-foreground leading-none"
            >
              {servings}
            </motion.span>
            <button
              onClick={inc}
              data-testid="increase-servings-btn"
              disabled={servings >= 24}
              aria-label="Increase servings"
              className="w-9 h-9 rounded-full bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-40 flex items-center justify-center transition-opacity duration-300"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          <div className="w-px h-8 bg-border" aria-hidden />
          <button
            onClick={handleShare}
            data-testid="share-recipe-btn"
            aria-label="Share this recipe"
            title="Share this recipe with servings preserved"
            className="w-9 h-9 rounded-full bg-secondary hover:bg-foreground hover:text-background flex items-center justify-center transition-colors duration-300"
          >
            <AnimatePresence mode="wait" initial={false}>
              {copied ? (
                <motion.span
                  key="check"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Check className="w-4 h-4 text-accent" strokeWidth={2} />
                </motion.span>
              ) : (
                <motion.span
                  key="share"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Share2 className="w-4 h-4" strokeWidth={1.5} />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
        {servings !== baseServings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-2 text-center font-serif-alt italic text-xs text-foreground/60"
            data-testid="scaled-from-note"
          >
            Scaled from {baseServings}
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
